import React from 'react';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone';
import {
	ApButtonMain,
	ApIcon,
	ApButtonSecondary,
	colors,
} from '@alixpartners/ui-components';
import { Telemetry } from '@alixpartners/ui-utils';

import {
	validateFirstFile,
	classifyFile,
	validateFile,
} from 'utils/apiRequests';
import { ApSignalrConsumer } from '@alixpartners/ui-utils';
import { UPLOAD_STATUS } from '../../constants';
import UploadProgressBar from '../UploadProgressBar';
import ErrorList from '../ErrorList';
import isEqual from 'lodash/isEqual';
import {
	DropZoneArea,
	DropZoneText,
	DropWrapper,
	DescriptionText,
	IconWrapper,
	TitleMessage,
	ActionsWrapper,
	ButtonWrapper,
	LeftAlignBox,
} from './styles';

class UploadData extends React.Component {
	static propTypes = {
		status: PropTypes.object.isRequired,
		fetchState: PropTypes.func.isRequired,
		setFileUploadingState: PropTypes.func.isRequired,
		isHarmonised: PropTypes.bool,
	};

	dropzoneRef = React.createRef();

	state = {
		uploadFailed: false,
		isFailed: this.props.status.state === UPLOAD_STATUS.FAILED,
		isProcessing:
			this.props.status.state === UPLOAD_STATUS.UPLOADED &&
			(!this.props.status.errors || this.props.status.errors.length === 0),
		statusMessage: '',
		status: this.props.status,
		currentUploadStep: 0,
		totalUploadSteps: 1,
		errors: this.props.status.errors || [],
	};
	componentDidMount() {
		if (this.props.normalUrlInfo !== undefined) {
			const { id } = this.props.normalUrlInfo;
			const { newhistory } = this.props;
			newhistory.replace(newhistory.location.pathname, undefined);
			const { fetchState, setFileUploadingState, isDirect } = this.props;

			this.setState({
				isFailed: false,
				uploadFailed: false,
				isProcessing: true,
				statusMessage: '',
				currentUploadStep: 0,
				totalUploadSteps: 1,
				errors: [],
			});
			setFileUploadingState(true);
			validateFile(id, isDirect)
				.then(resp => {
					const isValid = !resp.errors || resp.errors.length === 0;
					if (isDirect && isValid) {
						classifyFile(resp.fileName, isDirect).finally(() => {
							fetchState();
						});
					} else {
						fetchState();
					}
				})
				.catch(() => {
					setFileUploadingState(false);
					this.setState({
						isProcessing: false,
						uploadFailed: true,
					});
				});

			Telemetry.trackEvent('file selected');
		}
	}
	onFileSelected = acceptedFiles => {
		const file = acceptedFiles[0];
		const {
			fetchState,
			setFileUploadingState,
			isDirect,
			isHarmonised,
			getCurrentEtlState,
		} = this.props;

		this.setState({
			isFailed: false,
			uploadFailed: false,
			isProcessing: true,
			statusMessage: '',
			currentUploadStep: 0,
			totalUploadSteps: 1,
			errors: [],
		});
		setFileUploadingState(true);
		validateFirstFile(file, isDirect)
			.then(resp => {
				const isValid = !resp.errors || resp.errors.length === 0;

				if (isDirect && isValid && isHarmonised) {
					classifyFile(resp.fileName, isDirect).finally(() => {
						fetchState();
					});
				} else {
					fetchState();
				}
				getCurrentEtlState();
			})
			.catch(() => {
				setFileUploadingState(false);
				this.setState({
					isProcessing: false,
					uploadFailed: true,
				});
				getCurrentEtlState();
			});

		Telemetry.trackEvent('file selected');
	};

	componentDidUpdate(prevProps) {
		const { status } = this.props;

		if (!isEqual(prevProps.status, status)) {
			this.setState({
				isFailed: status.state === UPLOAD_STATUS.FAILED,
				isProcessing:
					status.state === UPLOAD_STATUS.UPLOADED &&
					(!status.errors || status.errors.length === 0),
				errors: status.errors || [],
				status,
			});
		}
	}

	renderProcessingBlock = () => {
		const { statusMessage, currentUploadStep, totalUploadSteps } = this.state;

		return (
			<UploadProgressBar
				label={statusMessage || 'Uploading your data into the secure cloud'}
				value={Math.floor((100 * currentUploadStep) / totalUploadSteps)}
			/>
		);
	};

	renderUnsuccessBlock = message => {
		const { ETLCurrentState, ETLRestart } = this.props;
		Telemetry.trackEvent('showing unsuccessful upload message');

		return (
			<React.Fragment>
				<LeftAlignBox>
					<TitleMessage>
						{message || 'The import of the file has failed, see details below'}
					</TitleMessage>
					<ActionsWrapper>
						<ApButtonSecondary
							iconName={ETLCurrentState === 10 ? '' : 'baseline_add'}
							onClick={() => {
								if (ETLCurrentState === 10) {
									ETLRestart();
								}
							}}
						>
							{ETLCurrentState === 10 ? 'Restart' : 'Add New file'}
						</ApButtonSecondary>
					</ActionsWrapper>
				</LeftAlignBox>
			</React.Fragment>
		);
	};

	renderInitialBlock = () => {
		Telemetry.trackEvent('showing upload file');
		return (
			<React.Fragment>
				<IconWrapper>
					<ApIcon
						iconName="cloud_upload"
						iconSize="96px"
						iconColor={colors.grey3}
					/>
				</IconWrapper>
				<DropZoneText>
					<DescriptionText>
						Drag <span>.csv</span> file here or browse files to upload
					</DescriptionText>
					<ButtonWrapper>
						<ApButtonMain iconName="baseline_add">Add file</ApButtonMain>
					</ButtonWrapper>
				</DropZoneText>
			</React.Fragment>
		);
	};

	render() {
		const { isProcessing, isFailed, uploadFailed, errors } = this.state;
		const { ETLCurrentState } = this.props;
		const { fileName, fileSize, rowsWithErrors } = this.props.status;
		const isInitial = errors.length === 0 && !isFailed && !uploadFailed;

		if (isProcessing) {
			return this.renderProcessingBlock();
		}

		return (
			<React.Fragment>
				<Dropzone
					ref={this.dropzoneRef}
					onDrop={this.onFileSelected}
					style={{}}
					noKeyboard
				>
					{({ getRootProps, getInputProps }) => (
						<DropWrapper {...getRootProps()}>
							<input
								disabled={
									(ETLCurrentState === 10 && errors.length !== 0) ||
									(ETLCurrentState === 10 && isFailed) ||
									(ETLCurrentState === 10 && uploadFailed)
								}
								{...getInputProps()}
							/>
							<DropZoneArea>
								{errors.length === 0 && !isFailed ? (
									<React.Fragment>
										{uploadFailed && this.renderUnsuccessBlock('Upload Failed')}
										{isInitial && this.renderInitialBlock()}
									</React.Fragment>
								) : (
									this.renderUnsuccessBlock(
										isFailed
											? 'The classifier has failed. Administrators have been notified and will look into the issue'
											: ''
									)
								)}
							</DropZoneArea>
						</DropWrapper>
					)}
				</Dropzone>

				{errors.length > 0 && (
					<ErrorList
						rowsWithErrors={rowsWithErrors}
						errors={errors}
						fileSize={fileSize}
						fileName={fileName}
					/>
				)}

				<ApSignalrConsumer
					message="UploadProcessing"
					onMessage={(
						messageName,
						statusMessage,
						currentUploadStep,
						totalUploadSteps
					) => {
						this.setState({
							statusMessage,
							currentUploadStep,
							totalUploadSteps,
							isProcessing: errors.length === 0,
						});
					}}
				/>
				<ApSignalrConsumer
					message="UploadCompleted"
					onMessage={() => {
						this.setState(
							{
								statusMessage: '',
								isProcessing: false,
							},
							() => this.props.fetchState()
						);
					}}
				/>
				<ApSignalrConsumer
					message="UploadFailed"
					onMessage={() => {
						this.setState({
							isFailed: true,
							isProcessing: false,
						});
						this.props.fetchState();
					}}
				/>
			</React.Fragment>
		);
	}
}

export default UploadData;
